import { memo, useCallback, useRef, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn, InputCode } from '@divlab/divanui';

import { useRequest } from '@Contexts/Request';
import useTranslation from '@Queries/useTranslation';
import validationSchema from './schema';
import InputPhone from '@Components/InputPhone';
import ResendCodeTimer from '@Components/ResendCodeTimer';
import FormItem from '@UI/FormItem';
import Button from '@UI/Button';
import UILink from '@UI/Link';
import Form from '@UI/Form';
import useRouteMatchers from '@Navigation/useRouteMatchers';
import useNavigation from '@Navigation/useNavigation';
import { useDeps } from '@Contexts/DI';
import styles from './AuthorizationForm.module.css';

import type { ApiResponse } from '@Types/Api';
import type { FC, ChangeEvent, MouseEvent } from 'react';
import type { AuthType, ProfileData } from '@Types/Profile';
import type { FormProps, ServerErrorsOpts } from '@UI/Form';
import type { FormData } from '../../Form';

interface AuthorizationFormResponse {
  profile: ProfileData;
  needClarification: boolean;
}

interface AuthorizationFormProps extends Omit<FormProps, 'children'> {
  defaultData: FormData;
  onChangeData?(e: ChangeEvent<HTMLInputElement>): void;
  onSuccess?(profile: ProfileData, needClarification: boolean): void;
  onResetPhone?(): void;
  onCodeSent?(): void;
}

type Step = 'get-auth-code' | 'login';

const AuthorizationForm: FC<AuthorizationFormProps> = (props) => {
  const {
    className,
    defaultData,
    onChangeData,
    onSuccess,
    onResetPhone,
    onCodeSent,
    ...restProps
  } = props;
  const { phone } = defaultData;
  const { t } = useTranslation();
  const { isOrderCheck, isDivanClub } = useRouteMatchers();
  const { country } = useRequest();
  const queryClient = useQueryClient();
  const { logger, analytics } = useDeps();
  const {
    params: { slug },
  } = useNavigation();
  const [serverErrors, setServerErrors] = useState([]);
  const [step, setStep] = useState<Step>('get-auth-code');
  const [waiting, setWaiting] = useState(false);
  const [waitingRepeat, setWaitingRepeat] = useState(false);
  const [keyRepeat, setKeyRepeat] = useState(1);
  const [disabled, setDisabled] = useState(false);
  const [disabledButton, setDisabledButton] = useState(!phone);
  const [resendCode, setResendCode] = useState(false);
  const [authType] = useState<AuthType>('sms');
  const [authToken] = useState('');
  const submitButton = useRef<HTMLButtonElement>(null);
  const isStepLogin = step === 'login';
  const isStepGetCode = step === 'get-auth-code';

  const pointOfContact = useMemo(() => {
    if (isOrderCheck) return 'CartOrOrder';
    if (isDivanClub) return 'Landing';

    return 'Other';
  }, [isOrderCheck, isDivanClub]);

  const triggerSubmit = useCallback(() => {
    if (!submitButton.current) return;

    setTimeout(() => {
      submitButton.current.click();
    }, 200);
  }, []);

  const handleSubmit = () => {
    setWaiting(true);
    setDisabledButton(true);
  };

  const handleResponse = useCallback(
    async (res: ApiResponse<AuthorizationFormResponse>) => {
      setWaiting(false);

      if (!res.ok) {
        setDisabledButton(true);

        const errors: ServerErrorsOpts[] = (res as any).errors;
        if (errors?.length > 0) {
          const transformedErrors = errors.map((err) => ({ ...err, name: err.field || 'phone' }));

          setServerErrors(transformedErrors);
        }
        return;
      }

      if (resendCode) {
        setKeyRepeat((prev) => prev + 1);
        setResendCode(false);
        setWaitingRepeat(false);
        return;
      }

      if (onCodeSent) onCodeSent();

      if (isStepLogin) {
        setDisabled(true);

        analytics.dispatchEvent('profile.signIn', { action: 'send', type: 'phoneNumber' });
        await queryClient.invalidateQueries({ queryKey: ['profile'] });

        if (onSuccess) onSuccess(res.data.profile, res.data.needClarification);
      }

      if (isStepGetCode) setStep('login');
    },
    [resendCode, onCodeSent, isStepLogin, isStepGetCode, analytics, queryClient, onSuccess],
  );

  const handleCriticalError = (err: unknown) => {
    logger.log(err);

    setDisabledButton(true);
    setWaiting(false);
  };

  const handleRepeatSendCode = useCallback(async () => {
    setDisabledButton(false);
    setWaitingRepeat(true);
    setResendCode(true);

    triggerSubmit();
  }, [triggerSubmit]);

  const handleResetPhone = (event: MouseEvent) => {
    event.preventDefault();
    setDisabledButton(true);
    setStep('get-auth-code');
  };

  const handleChangeCode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChangeData) onChangeData(event);
      setDisabledButton(false);
    },
    [onChangeData],
  );

  const handleChangePhone = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const number = event.target.value;
      const isFullNumber = !number.match(/_/g)?.length && number !== '';

      if (onChangeData) onChangeData(event);

      setDisabledButton(!isFullNumber);
    },
    [onChangeData],
  );

  const handleClearPhone = () => {
    if (onResetPhone) onResetPhone();
  };

  const handleCompletePhone = useCallback(() => {
    setDisabledButton(false);
  }, []);

  return (
    <Form
      {...restProps}
      className={cn(styles.container, className)}
      action='/cabinet/login'
      name='authorization'
      validationSchemaBuilder={validationSchema}
      serverErrors={serverErrors}
      onSubmit={handleSubmit}
      onResponse={handleResponse}
      onCriticalError={handleCriticalError}
    >
      <div className={styles.form}>
        <input hidden name='resendCode' value={resendCode.toString()} />
        <input hidden name='pointOfContact' value={pointOfContact} />
        <input hidden name='country' value={country} />
        <input hidden name='type' value={authType} />
        <input hidden name='token' value={authToken} />
        <input hidden name='url' value={isDivanClub ? slug : undefined} />

        <FormItem className={styles.formItem}>
          <InputPhone
            placeholder={t('form.phone')}
            name='phone'
            defaultValue={phone}
            readOnly={waiting || disabled || isStepLogin}
            mode='redesign'
            onChange={handleChangePhone}
            onComplete={handleCompletePhone}
            onReset={handleClearPhone}
            className={cn(styles.phoneInput, 'custom-input')}
            cnControl={styles.smallPaddings}
            cnPlaceholder={styles.smallPaddings}
          />
          {isStepLogin && (
            <div className={styles.phoneBottom}>
              <UILink className={styles.linkChangePhone} view='primary' onClick={handleResetPhone}>
                {t('form.change-phone')}
              </UILink>
            </div>
          )}
        </FormItem>

        {isStepLogin && (
          <>
            <FormItem
              className={styles.rowCode}
              bottom={
                <ResendCodeTimer
                  key={keyRepeat}
                  className={styles.timer}
                  waiting={waiting || waitingRepeat}
                  seconds={60}
                  onRepeat={handleRepeatSendCode}
                />
              }
            >
              <InputCode
                autoFocus
                readOnly={waiting || waitingRepeat || disabled}
                name='code'
                key={keyRepeat}
                onComplete={triggerSubmit}
                onInput={handleChangeCode}
                className={styles.inputCode}
              />
            </FormItem>
          </>
        )}

        <div className={cn(styles.buttons, { [styles.visible]: isStepGetCode })}>
          <Button wide theme='primary' type='submit' waiting={waiting} disabled={disabledButton}>
            {t('form.registration')}
          </Button>
          <button hidden type='submit' ref={submitButton}></button>
        </div>
      </div>
    </Form>
  );
};

export default memo(AuthorizationForm);
